import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useDomainStore, useNotificationPreferencesStore } from '@/hooks/useStores';
import { FamilyMetadataType } from '@/stores/DomainStore';
import { formatRelativeTime } from '@/utils/formatRelativeTime';
import { getRoleDisplayName } from '@/utils/rolePermissions';
import { getFamilyPlanPillLabel, isFamilyOwnerOrAdmin, normalizeFamilyPlan } from '@/utils/familyPlan';
import Close from '@/icons/Close';
import Pencil from '@/icons/Pencil';
import { BellRing } from '@/icons';

interface FamilyCardProps {
  family: FamilyMetadataType;
  onEditName?: () => void;
  /** First card only — anchors the families-list tour rename step. */
  renameTourAnchor?: boolean;
}

function rolePillModifier(role: string | null | undefined): string {
  if (!role) return 'viewer';
  if (role === 'admin') return 'admin';
  return role;
}

const FamilyCard: React.FC<FamilyCardProps> = observer(({ family, onEditName, renameTourAnchor }) => {
  const { t } = useTranslation('common');
  const { t: tScreens } = useTranslation('screens');
  const { t: tPedigree } = useTranslation('pedigree');
  const navigate = useNavigate();
  const domainStore = useDomainStore();
  const notificationPrefsStore = useNotificationPreferencesStore();
  const [isDeleting, setIsDeleting] = useState(false);

  const displayName = family.displayName || family.name;
  const roleKey = rolePillModifier(family.userRole);
  const lastActivityBy = family.lastActivityBy || t('someone');
  const showPlanPill = isFamilyOwnerOrAdmin(family.userRole);
  const planKey = normalizeFamilyPlan(family.plan);
  const unreadCount = notificationPrefsStore.unreadForFamily(family.id);
  const showToolbar = unreadCount > 0 || !!family.canDeleteFamily;

  const handleCardClick = () => {
    navigate(`/families/${family.id}/overview`);
  };

  const handleUnreadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(`/families/${family.id}/activity`);
  };

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onEditName?.();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const confirmed = window.confirm(tScreens('familyCard.deleteConfirm', { name: displayName }));
    if (!confirmed) return;

    setIsDeleting(true);
    domainStore
      .deleteFamily(family.id)
      .catch((err: Error & { getUserMessage?: () => string }) => {
        const message = err?.getUserMessage?.() ?? err?.message ?? tScreens('familyCard.deleteFailed');
        window.alert(message);
      })
      .finally(() => setIsDeleting(false));
  };

  return (
    <div
      className="family-card"
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      <span className="family-card__accent" aria-hidden />
      {showToolbar ? (
        <div className="family-card__toolbar">
          {unreadCount > 0 ? (
            <button
              type="button"
              className="family-card__unread"
              title={tScreens('familyCard.unreadTitle', { count: unreadCount })}
              aria-label={tScreens('familyCard.unreadAria', { name: displayName, count: unreadCount })}
              onClick={handleUnreadClick}
            >
              <BellRing width={14} height={14} />
              <span className="family-card__unread-count">{unreadCount}</span>
            </button>
          ) : null}
          {family.canDeleteFamily ? (
            <button
              type="button"
              className="family-card__delete card-hover-remove"
              title={tScreens('familyCard.deleteFamily')}
              aria-label={tScreens('familyCard.deleteAria', { name: displayName })}
              disabled={isDeleting}
              onClick={handleDeleteClick}
            >
              <Close width={15} height={15} />
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="family-card__head">
        <div className="family-card__name-row">
          <h3 className="family-card__name">{displayName}</h3>
          {onEditName ? (
            <button
              type="button"
              className="profile-entry-icon-btn family-card__rename"
              title={tScreens('familyCard.renameTitle')}
              aria-label={tScreens('familyCard.renameAria', { name: displayName })}
              data-tour={renameTourAnchor ? 'family-card-rename' : undefined}
              onClick={handleRenameClick}
            >
              <Pencil />
            </button>
          ) : null}
        </div>
      </div>
      {family.userRole || showPlanPill ? (
        <div className="family-card__pills">
          {showPlanPill ? (
            <span className={`plan-pill plan-pill--${planKey}`} title={getFamilyPlanPillLabel(family.plan)}>
              {getFamilyPlanPillLabel(family.plan)}
            </span>
          ) : null}
          {family.userRole ? (
            <span className={`role-pill role-pill--${roleKey}`}>{getRoleDisplayName(family.userRole)}</span>
          ) : null}
        </div>
      ) : null}
      <div className="family-card__stats">
        <span className="family-card__stat">
          <strong>{(family.memberCount || 0).toLocaleString()}</strong> {tPedigree('membersLabel')}
        </span>
      </div>
      <div className="family-card__foot">
        <FamilyCardMeta lastActivityAt={family.lastActivityAt} lastActivityBy={lastActivityBy} />
      </div>
    </div>
  );
});

function FamilyCardMeta({ lastActivityAt, lastActivityBy }: { lastActivityAt?: string; lastActivityBy: string }) {
  const { t } = useTranslation('common');

  return (
    <div className="family-card__meta">
      <span className="family-card__meta-line">
        {t('meta.lastUpdatedBy')}
        <strong>{lastActivityBy}</strong>
      </span>
      <span className="family-card__meta-line">
        {t('meta.lastUpdatedOn')}
        <strong>{formatRelativeTime(lastActivityAt)}</strong>
      </span>
    </div>
  );
}

export default FamilyCard;
